// On app mount, authenticated product routes attempt /auth/refresh for BOTH
// principal kinds. Public demo/share routes skip refresh so they cannot silently
// reuse a previous person's session on a shared device, and the teacher handoff
// page skips it so it cannot race the handoff redeem. Every path sets
// `bootstrapped: true` once its bootstrap decision settles.

import { useEffect } from 'react';

import { refreshAccessToken, SESSION_BOOTSTRAP_DEADLINE_MS } from '@/lib/api';
import { getSocket } from '@/lib/ws';
import { useAuthStore } from './authStore';

// The Teacher Console handoff page owns session setup: it redeems a one-shot
// token that issues a fresh teacher session, then reloads into the target. A
// bootstrap refresh here would race that redeem with whatever app cookie the
// browser already holds (e.g. a parent signed in on the same browser). If the
// refresh lands last, it rotates the OLD cookie back in and the teacher arrives
// as that other user — every /teacher/* call then answers 403.
const HANDOFF_PATHS = new Set(['/teacher/handoff']);

export function skipsAuthBootstrap(pathname: string): boolean {
  return (
    HANDOFF_PATHS.has(pathname) ||
    pathname === '/labs/chemistry' ||
    pathname === '/labs/chemistry/' ||
    pathname.startsWith('/try/') ||
    pathname.startsWith('/experiments/story-blocks/') ||
    pathname === '/play' ||
    pathname.startsWith('/play/')
  );
}

export function useBootstrap(): void {
  useEffect(() => {
    let cancelled = false;
    let settled = false;
    // The restore carries its own deadline. The route guards render the splash
    // until `bootstrapped` flips, and this surface awaits BOTH principal kinds,
    // so one stalled /auth/refresh would keep a parent and a kid on that splash
    // indefinitely with the sign-in page out of reach — the 2026-09-21
    // admin-console incident, same defect. The deadline is the safety net, not
    // the happy path: a healthy refresh answers in well under a second.
    const settle = () => {
      if (cancelled || settled) return;
      settled = true;
      useAuthStore.getState().setBootstrapped(true);
    };
    const deadline = setTimeout(settle, SESSION_BOOTSTRAP_DEADLINE_MS);
    (async () => {
      // Public demo and share routes are anonymous BY DESIGN. A /play link must
      // never restore a previous person's refresh cookie on a shared device:
      // the visitor explicitly signs in after choosing Remix, and the in-memory
      // token then survives the same-SPA returnTo round trip.
      if (skipsAuthBootstrap(window.location.pathname)) {
        clearTimeout(deadline);
        settle();
        return;
      }
      const [, kidToken] = await Promise.all([
        refreshAccessToken('user'),
        refreshAccessToken('kid'),
      ]);
      clearTimeout(deadline);
      if (cancelled) return;
      // Only the kid surface uses WS today; spin it up if the kid session restored.
      if (kidToken) {
        setTimeout(() => getSocket('kid'), 0);
      }
      settle();
    })();
    return () => {
      cancelled = true;
      clearTimeout(deadline);
    };
  }, []);
}
